import Layout from '../../components/Layout';
import ProductCard from '../../components/ProductCard';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const products = [
  {
    name: "Vitamin D3",
    description: "Essential for bone health, immune function, and mood regulation. Many people are deficient.",
    bestFor: "General Wellness",
    benefits: ["Supports bone health", "Immune support", "Mood regulation"]
  },
  {
    name: "Omega-3 Fish Oil",
    description: "Supports heart health, brain function, and helps reduce inflammation.",
    bestFor: "Heart & Brain Health",
    benefits: ["Heart health support", "Brain function", "Anti-inflammatory"]
  },
  {
    name: "Magnesium Glycinate",
    description: "A well-absorbed form of magnesium that supports sleep, muscles, and stress response.",
    bestFor: "Sleep & Relaxation",
    benefits: ["Better sleep quality", "Muscle relaxation", "Stress support"]
  },
  {
    name: "Probiotic",
    description: "Supports gut health and digestion with beneficial bacteria strains.",
    bestFor: "Digestive Health",
    benefits: ["Gut microbiome support", "Better digestion", "Immune support"]
  },
  {
    name: "Vitamin B Complex",
    description: "Supports energy production, nervous system function, and overall vitality.",
    bestFor: "Energy & Vitality",
    benefits: ["Energy support", "Nervous system health", "Metabolism support"]
  },
  {
    name: "Zinc",
    description: "Important for immune function, wound healing, and skin health.",
    bestFor: "Immune Support",
    benefits: ["Immune function", "Skin health", "Wound healing"]
  }
];

const Supplements = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="supplements-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/wellness" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Wellness
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Wellness
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Supplements Guide
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            A curated list of popular supplements that may support your overall health. Remember: supplements complement—they don't replace—a balanced diet and healthy lifestyle.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            About Supplements
          </h2>
          <p className="text-[#374151] leading-relaxed mb-4">
            Supplements can help fill nutritional gaps, but they work best as part of an overall healthy lifestyle. Before starting any supplement regimen, it's wise to consult with a healthcare provider, especially if you have existing health conditions or take medications.
          </p>
          <p className="text-[#374151] leading-relaxed">
            The supplements below are some of the most commonly recommended and researched options for general wellness support.
          </p>
        </div>
      </section>

      {/* Products */}
      <section data-testid="supplements-products" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Popular Supplements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            How to Choose
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Third-party tested:</strong> Look for supplements verified by NSF, USP, or ConsumerLab.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Check for interactions:</strong> Some supplements interact with medications. Consult your doctor.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Start with basics:</strong> Vitamin D, omega-3, and magnesium are good starting points for most people.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Consider your diet:</strong> You may already get enough of certain nutrients from food.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Get tested:</strong> Blood tests can reveal actual deficiencies so you can supplement strategically.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice />
        </div>
      </section>
    </Layout>
  );
};

export default Supplements;
